import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Badge, Box, Button, Chip, CircularProgress, Divider, IconButton, List, ListItemButton, Popover, Tooltip, Typography
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { extractErrorMessage } from '../Services/httpClient';
import { getUnreadCount, listAlerts, markAlertRead, markAllAlertsRead } from '../Services/AlertsApi';
import { catalogItemFromLink, formatWhen, POLL_INTERVAL_MS, severityColor, severityLabel } from '../Services/alertsModel';

const ORANGE = '#f77513';

/**
 * O sino do backoffice (spec alertas-operacionais-backoffice.md, seção 6): contador de avisos não lidos, lista e atalho para o que precisa de
 * atenção. O contador é consultado a cada minuto e ao voltar para a aba; uma falha da consulta NÃO aparece (o contador anterior fica) e a lista,
 * quando aberta, mostra o erro.
 */
const AlertsBell = ({ onOpenItem }) => {
    const [unread, setUnread] = useState(0);
    const [anchor, setAnchor] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const mounted = useRef(true);

    const refreshCount = useCallback(async () => {
        try {
            const count = await getUnreadCount();
            if (mounted.current) setUnread(count);
        } catch {
            // Silencioso de propósito: o sino não pode encher a tela de erros por uma consulta que se repete a cada minuto.
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        refreshCount();
        const interval = setInterval(() => { if (!document.hidden) refreshCount(); }, POLL_INTERVAL_MS);
        const onVisible = () => { if (!document.hidden) refreshCount(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            mounted.current = false;
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [refreshCount]);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await listAlerts({ limit: 30 });
            if (!mounted.current) return;
            setAlerts(result.alerts);
            setUnread(result.unread);
        } catch (err) {
            if (mounted.current) setError(extractErrorMessage(err));
        } finally {
            if (mounted.current) setIsLoading(false);
        }
    }, []);

    const handleOpen = (event) => {
        setAnchor(event.currentTarget);
        load();
    };

    const handleClick = async (alert) => {
        setAnchor(null);
        const itemId = catalogItemFromLink(alert.link);
        if (!alert.read) {
            try {
                await markAlertRead(alert.id);
            } catch {
                // Abrir o item importa mais do que marcar como lido; o contador se acerta na próxima consulta.
            }
            refreshCount();
        }
        if (itemId) onOpenItem?.(itemId);
    };

    const handleMarkAll = async () => {
        setError(null);
        try {
            await markAllAlertsRead();
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    return (
        <>
            <Tooltip title={unread > 0 ? `${unread} aviso(s) não lido(s)` : 'Avisos'}>
                <IconButton onClick={handleOpen} sx={{ color: 'white' }} aria-label="Avisos">
                    <Badge badgeContent={unread} max={99} color="error">
                        <NotificationsIcon />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }} PaperProps={{ sx: { width: 420, maxWidth: '95vw', borderRadius: '14px' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25 }}>
                    <Typography variant="subtitle1" sx={{ color: ORANGE, fontWeight: 'bold', flexGrow: 1 }}>Avisos</Typography>
                    <Button size="small" disabled={unread === 0} onClick={handleMarkAll} style={unread === 0 ? undefined : { color: ORANGE }}>
                        Marcar todas como lidas
                    </Button>
                </Box>
                <Divider />

                {error && <Alert severity="error" sx={{ m: 1.5 }}>{error}</Alert>}
                {isLoading && alerts.length === 0 && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} style={{ color: ORANGE }} /></Box>}
                {!isLoading && !error && alerts.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>Nenhum aviso por enquanto.</Typography>
                )}

                <List disablePadding sx={{ maxHeight: 440, overflowY: 'auto' }}>
                    {alerts.map((alert) => (
                        <ListItemButton key={alert.id} onClick={() => handleClick(alert)} divider alignItems="flex-start"
                            sx={{ backgroundColor: alert.read ? 'transparent' : '#fff4ec', gap: 1.5 }}>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                    <Chip size="small" label={severityLabel(alert.severity)} color={severityColor(alert.severity)} />
                                    <Typography variant="caption" color="text.secondary">{formatWhen(alert.createdAt)}</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: alert.read ? 400 : 700 }}>{alert.title}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{alert.body}</Typography>
                                {catalogItemFromLink(alert.link) && (
                                    <Typography variant="caption" sx={{ color: ORANGE, fontWeight: 600 }}>Abrir o item</Typography>
                                )}
                            </Box>
                        </ListItemButton>
                    ))}
                </List>
            </Popover>
        </>
    );
};

export default AlertsBell;
